import React from 'react';
import { motion } from 'framer-motion';
import moment from 'moment';

const ProgressHistory = ({ progressData, theme }) => {
  // Process progress data
  const processChartData = () => {
    const scores = progressData || {};
    let keys = Object.keys(scores);
    
    // Sort by date
    keys.sort((a, b) => 
      moment(a, "YYYY-MM-DD").toDate().getTime() - 
      moment(b, "YYYY-MM-DD").toDate().getTime()
    );
    
    // Get last 6 data points
    if (keys.length > 6) {
      keys = keys.slice(keys.length - 6, keys.length);
    }
    
    const dataPoints = keys.map(key => ({
      date: moment(key, "YYYY-MM-DD").format("MMM YYYY"),
      shortDate: moment(key, "YYYY-MM-DD").format("DD MMM"),
      score: parseFloat(scores[key]) || 0,
      timestamp: key
    }));
    
    return dataPoints;
  };

    const dataPoints = processChartData();
  const maxScore = dataPoints.length > 0 ? Math.max(...dataPoints.map(d => d.score)) : 100;
  console.log("ProgressHistory -> maxScore", maxScore);
  
  const roundUpToGrid = (score) => {
    if (score <= 5) return 20;
    if (score <= 10) return 20;
    if (score <= 15) return 30;
    if (score <= 20) return 30;
    if (score <= 25) return 40;
    if (score <= 30) return 40;
    if (score <= 35) return 50;
    if (score <= 40) return 50;
    if (score <= 45) return 60;
    if (score <= 50) return 60;
    if (score <= 60) return 70;
    if (score <= 70) return 80;
    if (score <= 80) return 90;
    if (score <= 90) return 90;
    return 100;
  };
  // Set grid max value
  const gridMax = roundUpToGrid(maxScore);
  
  // Calculate grid line values - go up to just above maxScore
  const getGridLineValues = () => {
    // Round up to the next multiple of 5 or 10 for better grid spacing
    
    
    // Generate grid lines based on the rounded max value
    /*  if (gridMax <= 20) {
      return Array.from({ length: Math.floor(gridMax / 5) + 1 }, (_, i) => i * 5);
    } else {
      return Array.from({ length: Math.floor(gridMax / 10) + 1 }, (_, i) => i * 10);
    } */
    return Array.from({ length: Math.floor(gridMax / 10) + 1 }, (_, i) => i * 10);
  };
  
  const gridLineValues = getGridLineValues();
  console.log("ProgressHistory -> gridLineValues", gridLineValues);
  const chartHeight = 300;
  const chartWidth = 600;
  const padding = { top: 20, right: 50, bottom: 40, left: 50 };

  const styles = {
    container: {
      background: theme?.background?.card,
      borderRadius: '1.25rem',
      padding: '2rem',
      border: `1px solid ${theme?.borders?.primary}`,
      boxShadow: theme?.shadows?.base,
    },
    title: {
      fontSize: '1.25rem',
      fontWeight: '600',
      color: theme?.text?.primary,
      marginBottom: '1.5rem',
      textAlign: 'center',
    },
    chartContainer: {
      width: '100%',
      height: '100%',
      position: 'relative',
    },
    svg: {
      width: '100%',
      height: '100%',
    },
    noDataMessage: {
      textAlign: 'center',
      color: theme?.text?.secondary,
      fontSize: '0.875rem',
      padding: '3rem 0',
    },
  };

  if (dataPoints.length === 0) {
    return (
      <motion.div
        style={styles.container}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <h2 style={styles.title}>Readiness Progress History</h2>
        <div style={styles.noDataMessage}>
          Start practicing to see your progress over time
        </div>
      </motion.div>
    );
  }

  // Calculate chart dimensions and scales
  const chartInnerWidth = chartWidth - padding.left - padding.right;
  const chartInnerHeight = chartHeight - padding.top - padding.bottom;
  
  const xScale = (index) => (index / (dataPoints.length - 1 || 1)) * chartInnerWidth + padding.left;
  const yScale = (value) => chartInnerHeight - (value / gridMax) * chartInnerHeight + padding.top;

  // Create path for line chart
  const linePath = dataPoints
    .map((point, index) => {
      const x = xScale(index);
      const y = yScale(point.score);
      return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
    })
    .join(' ');

  // Create gradient area path
  const areaPath = `${linePath} L ${xScale(dataPoints.length - 1)} ${chartInnerHeight + padding.top} L ${xScale(0)} ${chartInnerHeight + padding.top} Z`;

  return (
    <motion.div
      style={styles.container}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      <h2 style={styles.title}>Readiness Progress History</h2>
      
      <div style={styles.chartContainer}>
        <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} style={styles.svg}>
          <defs>
            <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={theme?.primary?.blue?.[500]} stopOpacity="0.3" />
              <stop offset="100%" stopColor={theme?.primary?.blue?.[500]} stopOpacity="0.05" />
            </linearGradient>
          </defs>

          {/* Grid lines */}
          {gridLineValues.map((value) => (
            <g key={value}>
              <line
                x1={padding.left}
                y1={yScale(value)}
                x2={chartWidth - padding.right}
                y2={yScale(value)}
                stroke={theme?.borders?.primary}
                strokeDasharray="2,2"
                opacity="0.5"
              />
              <text
                x={padding.left - 10}
                y={yScale(value)}
                fill={theme?.text?.secondary}
                fontSize="12"
                textAnchor="end"
                alignmentBaseline="middle"
              >
                {value}
              </text>
            </g>
          ))}

          {/* X-axis line */}
          <line
            x1={padding.left}
            y1={chartInnerHeight + padding.top}
            x2={chartWidth - padding.right}
            y2={chartInnerHeight + padding.top}
            stroke={theme?.borders?.primary}
            strokeWidth="2"
          />

          {/* Area fill */}
          <motion.path
            d={areaPath}
            fill="url(#scoreGradient)"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.5 }}
          />

          {/* Line */}
          <motion.path
            d={linePath}
            fill="none"
            stroke={theme?.primary?.blue?.[500]}
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1.5, ease: "easeInOut" }}
          />

          {/* Data points and labels */}
          {dataPoints.map((point, index) => {
            const x = xScale(index);
            const y = yScale(point.score);
            const isLast = index === dataPoints.length - 1;

            return (
              <g key={point.timestamp}>
                {/* Data point */}
                <motion.circle
                  cx={x}
                  cy={y}
                  r={isLast ? "8" : "6"}
                  fill={isLast ? theme?.primary?.orange?.[500] : theme?.primary?.blue?.[500]}
                  stroke="white"
                  strokeWidth="2"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.5 + index * 0.1, type: "spring", stiffness: 300 }}
                />

                {/* Value label */}
                <motion.text
                  x={x}
                  y={y - 15}
                  fill={isLast ? theme?.primary?.orange?.[500] : theme?.text?.secondary}
                  fontSize="12"
                  fontWeight={isLast ? "600" : "500"}
                  textAnchor="middle"
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 + index * 0.1 }}
                >
                  {point.score.toFixed(1)}%
                </motion.text>

                {/* Date label */}
                <text
                  x={x}
                  y={chartHeight - 10}
                  fill={theme?.text?.secondary}
                  fontSize="11"
                  textAnchor="middle"
                >
                  {point.shortDate}
                </text>
              </g>
            );
          })}

          {/* Current label */}
          {dataPoints.length > 0 && (
            <text
              x={xScale(dataPoints.length - 1)}
              y={chartHeight - 25}
              fill={theme?.primary?.orange?.[500]}
              fontSize="10"
              fontWeight="600"
              textAnchor="middle"
            >
              Today
            </text>
          )}
        </svg>
      </div>
    </motion.div>
  );
};

export default ProgressHistory;